import { PersonService } from "./person.service"
import type { Person, PersonLite } from "./person.models"

/**
 * <tbw-person-profile> — the /people/<slug> page body, loaded from GET /people/:slug.
 *
 * The Webflow template used to bind role, bio, LinkedIn and the colleagues list in
 * the page HTML, served to anyone: the /login redirect runs after the HTML is in, so a
 * script read every profile without an account (audit 15/10). The template now only
 * holds this element; the data needs a JWT and a Premium tier (403 for Free), the
 * same rule as the mobile apps.
 *
 * Markup reuses the template's Webflow classes so the page looks the same.
 */

export type Lang = "en" | "fr" | "de" | "nl"

const COPY: Record<Lang, {
  seeCompany: string; requestIntro: string; colleagues: string; content: string; events: string
  noColleagues: string; noContent: string; noEvents: string
  lockedTitle: string; lockedText: string; lockedCta: string; notFound: string; error: string; loading: string
}> = {
  en: {
    seeCompany: "See the company ↗", requestIntro: "Request an introduction",
    colleagues: "Colleagues", content: "Content", events: "Events",
    noColleagues: "No colleagues found.", noContent: "No content found.", noEvents: "No event participations found.",
    lockedTitle: "People profiles are reserved for members",
    lockedText: "Members see each leader's role, background, and an introduction through TBW.",
    lockedCta: "Become a member", notFound: "This profile does not exist.", error: "Unable to load this profile.", loading: "Loading...",
  },
  fr: {
    seeCompany: "Voir l'entreprise ↗", requestIntro: "Demander une introduction",
    colleagues: "Collègues", content: "Contenus", events: "Événements",
    noColleagues: "Aucun collègue trouvé.", noContent: "Aucun contenu trouvé.", noEvents: "Aucune participation à un événement.",
    lockedTitle: "Les profils sont réservés aux membres",
    lockedText: "Les membres voient le rôle et le parcours de chaque dirigeant, et peuvent demander une introduction via TBW.",
    lockedCta: "Devenir membre", notFound: "Ce profil n'existe pas.", error: "Impossible de charger ce profil.", loading: "Chargement...",
  },
  de: {
    seeCompany: "Zum Unternehmen ↗", requestIntro: "Vorstellung anfragen",
    colleagues: "Kollegen", content: "Inhalte", events: "Events",
    noColleagues: "Keine Kollegen gefunden.", noContent: "Keine Inhalte gefunden.", noEvents: "Keine Event-Teilnahmen gefunden.",
    lockedTitle: "Profile sind Mitgliedern vorbehalten",
    lockedText: "Mitglieder sehen Rolle und Werdegang jeder Führungskraft und können eine Vorstellung über TBW anfragen.",
    lockedCta: "Mitglied werden", notFound: "Dieses Profil existiert nicht.", error: "Profil konnte nicht geladen werden.", loading: "Wird geladen...",
  },
  nl: {
    seeCompany: "Bekijk het bedrijf ↗", requestIntro: "Vraag een introductie aan",
    colleagues: "Collega's", content: "Content", events: "Events",
    noColleagues: "Geen collega's gevonden.", noContent: "Geen content gevonden.", noEvents: "Geen eventdeelnames gevonden.",
    lockedTitle: "Profielen zijn voorbehouden aan leden",
    lockedText: "Leden zien de rol en achtergrond van elke leider en kunnen via TBW een introductie aanvragen.",
    lockedCta: "Word lid", notFound: "Dit profiel bestaat niet.", error: "Kan dit profiel niet laden.", loading: "Laden...",
  },
}

const INTRO_MAILTO = "mailto:benoit@thebigwhale.io;rbloch@thebigwhale.io"
const MEMBERSHIP_URL = "https://www.thebigwhale.io/contact"

const LINKEDIN_ICON = `<svg width="30" height="31" viewBox="0 0 30 31" fill="none" xmlns="http://www.w3.org/2000/svg"><rect y="0.250244" width="30" height="30" rx="15" fill="var(--seashell)"/><path d="M22 22.2502H19.0993V17.7031C19.0993 16.6187 19.0806 15.2233 17.592 15.2233C16.0825 15.2233 15.8519 16.4052 15.8519 17.6251V22.2502H12.9545V12.8991H15.7353V14.1773H15.7749C16.1616 13.4425 17.1082 12.6668 18.5199 12.6668C21.4572 12.6668 22 14.6027 22 17.1217V22.2502ZM9.68261 11.6208H9.68098C8.75878 11.6208 8 10.8606 8 9.93676V9.93472C8 9.01083 8.75919 8.25024 9.68139 8.25024H9.68261C10.1217 8.26191 10.539 8.44435 10.8462 8.75892C11.1533 9.07349 11.3262 9.49549 11.3281 9.93554C11.3262 10.3756 11.1533 10.7976 10.8462 11.1122C10.539 11.4267 10.1217 11.6092 9.68261 11.6208ZM11.1342 22.2502H8.22984V12.8991H11.1342V22.2502Z" fill="var(--midnight-blue)"/></svg>`

export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!))
}

/** Only http(s) URLs reach an href/src; anything else (javascript:, data:) is dropped. */
export function safeUrl(value: string | null | undefined): string | null {
  if (!value) return null
  try {
    const url = new URL(value, "https://app.thebigwhale.io")
    return url.protocol === "https:" || url.protocol === "http:" ? url.href : null
  } catch {
    return null
  }
}

/** /people/<slug>, /fr/people/<slug>, … → <slug>; null on any other path. */
export function slugFromPath(pathname: string): string | null {
  const match = pathname.match(/^(?:\/(?:fr|de|nl))?\/people\/([^/?#]+)\/?$/)
  return match ? decodeURIComponent(match[1]) : null
}

export function langFromPath(pathname: string): Lang {
  const prefix = pathname.split("/")[1]
  return prefix === "fr" || prefix === "de" || prefix === "nl" ? prefix : "en"
}

/** Locale prefix for in-app links, so a French reader stays on /fr/…. */
function localePath(lang: Lang, path: string): string {
  return lang === "en" ? path : `/${lang}${path}`
}

/** The bio is CMS rich text written by the team; still, drop anything executable. */
export function sanitizeBio(html: string): string {
  const template = document.createElement("template")
  template.innerHTML = html
  template.content.querySelectorAll("script, style, iframe, object, embed, form").forEach((el) => el.remove())
  template.content.querySelectorAll("*").forEach((el) => {
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase()
      if (name.startsWith("on")) el.removeAttribute(attr.name)
      if ((name === "href" || name === "src") && !safeUrl(attr.value)) el.removeAttribute(attr.name)
    }
  })
  return template.innerHTML
}

export function colleagueCard(c: PersonLite, coverUrl: string | null, lang: Lang): string {
  const avatar = safeUrl(c.avatarUrl)
  const cover = safeUrl(coverUrl)
  return `
    <div role="listitem" class="ocean-results-item">
      <div class="project-card">
        <div class="project-card-cover"${cover ? ` style="background-image:url(&quot;${escapeHtml(cover)}&quot;)"` : ""}>
          ${avatar ? `<img width="100" height="100" src="${escapeHtml(avatar)}" loading="lazy" alt="${escapeHtml(c.name)}" class="project_card_information_logo"/>` : ""}
          <div class="project-cover-blur"></div>
        </div>
        <div class="project_card-profile"><div class="project_information small">
          <div class="project_heading"><div class="subheadline1">${escapeHtml(c.name)}</div></div>
          <div class="truncate is-ocean-card"><p class="body3 is-medium">${escapeHtml(c.headline ?? "")}</p></div>
        </div></div>
        <a aria-label="people link" href="${escapeHtml(localePath(lang, `/people/${encodeURIComponent(c.slug)}`))}" class="image-card_full-background-link w-inline-block"></a>
      </div>
    </div>`
}

function linkList(items: { title: string; url: string; date: string | null }[], lang: Lang): string {
  return `<div class="tbw-person__links">${items.map((item) => {
    const url = safeUrl(item.url)
    const href = url ? localePath(lang, new URL(url).pathname) : "#"
    const date = item.date ? new Date(item.date) : null
    const label = date && !Number.isNaN(date.getTime())
      ? date.toLocaleDateString(lang, { year: "numeric", month: "short", day: "numeric" })
      : ""
    return `<a class="tbw-person__link" href="${escapeHtml(href)}"><span class="body3 is-medium">${escapeHtml(item.title)}</span>${label ? `<span class="caption1">${escapeHtml(label)}</span>` : ""}</a>`
  }).join("")}</div>`
}

export function emptyState(text: string): string {
  return `<div class="empty-state-13"><div class="subheadline2">${escapeHtml(text)}</div></div>`
}

export function renderProfile(person: Person, lang: Lang): string {
  const copy = COPY[lang]
  const avatar = safeUrl(person.avatarUrl)
  const linkedin = safeUrl(person.linkedin)
  const company = person.company
  const subject = encodeURIComponent(`Request for introduction: ${person.name}${company ? ` (${company.name})` : ""}`)
  const colleagues = person.colleagues ?? []
  const articles = (person.authoredArticles ?? []).map((a) => ({ title: a.title, url: a.url, date: a.publishedAt }))
  const events = person.events ?? []
  return `
    <div class="people-description">
      ${avatar ? `<div class="people-image"><img src="${escapeHtml(avatar)}" loading="lazy" alt="${escapeHtml(person.name)}" class="person-avatar"/></div>` : ""}
      <div class="biography-wrapper">
        <div class="page-navigation-top">
          <h1 class="headline-5">${escapeHtml(person.name)}</h1>
          <div class="body3">${escapeHtml(person.headline ?? "")}${company ? `${person.headline ? ", " : ""}<b>${escapeHtml(company.name)}</b>` : ""}</div>
        </div>
        <div class="button-company-redirection">
          ${company ? `<a href="${escapeHtml(localePath(lang, `/companies/${encodeURIComponent(company.slug)}`))}" class="button is-tertiary">${escapeHtml(copy.seeCompany)}</a>` : ""}
          <a href="${INTRO_MAILTO}?subject=${subject}" class="button is-tertiary">${escapeHtml(copy.requestIntro)}</a>
        </div>
        ${linkedin ? `<div class="flexbox_share small-gap"><div class="share-logo_embed"><a href="${escapeHtml(linkedin)}" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">${LINKEDIN_ICON}</a></div></div>` : ""}
        ${person.bio ? `<div class="body3 w-richtext">${sanitizeBio(person.bio)}</div>` : ""}
      </div>
    </div>
    <div class="people-tabs">
      <div class="tabs" role="tablist">
        <a class="button-select-v3 w-inline-block w--current" role="tab" data-tab="colleagues" href="#"><div>${escapeHtml(copy.colleagues)}</div></a>
        <a class="button-select-v3 w-inline-block" role="tab" data-tab="content" href="#"><div>${escapeHtml(copy.content)}</div></a>
        <a class="button-select-v3 w-inline-block" role="tab" data-tab="events" href="#"><div>${escapeHtml(copy.events)}</div></a>
      </div>
      <div class="tbw-person__pane" data-pane="colleagues">
        ${colleagues.length
          ? `<div class="ocean-results-wrapper"><div class="results-list-company-people" role="list">${colleagues.map((c) => colleagueCard(c, company?.logoUrl ?? null, lang)).join("")}</div></div>`
          : emptyState(copy.noColleagues)}
      </div>
      <div class="tbw-person__pane" data-pane="content" hidden>${articles.length ? linkList(articles, lang) : emptyState(copy.noContent)}</div>
      <div class="tbw-person__pane" data-pane="events" hidden>${events.length ? linkList(events, lang) : emptyState(copy.noEvents)}</div>
    </div>`
}

export function renderLocked(lang: Lang): string {
  const copy = COPY[lang]
  return `
    <div class="tbw-person__wall">
      <h2 class="headline-5">${escapeHtml(copy.lockedTitle)}</h2>
      <p class="body3">${escapeHtml(copy.lockedText)}</p>
      <a class="button" href="${MEMBERSHIP_URL}">${escapeHtml(copy.lockedCta)}</a>
    </div>`
}

export class PersonProfile extends HTMLElement {
  // Not `lang`: HTMLElement already has one (a string attribute).
  private readonly pageLang: Lang = langFromPath(window.location.pathname)

  constructor(private personService: PersonService = window.tbwServices.personService) {
    super()
  }

  async connectedCallback() {
    const slug = slugFromPath(window.location.pathname)
    if (!slug) return
    await this.waitForAuth()
    // Anonymous: render nothing. RouteGuardian sends them to /login anyway.
    if (!window.tbwServices.authService.isAuthenticated) return
    const copy = COPY[this.pageLang]
    this.innerHTML = `<p class="body3">${escapeHtml(copy.loading)}</p>`
    const result = await this.personService.get(slug, this.pageLang)
    if (result.status === "ok") {
      this.innerHTML = renderProfile(result.person, this.pageLang)
      this.bindTabs()
    } else if (result.status === "locked") {
      this.innerHTML = renderLocked(this.pageLang)
    } else {
      this.innerHTML = `<p class="body3">${escapeHtml(result.status === "not_found" ? copy.notFound : copy.error)}</p>`
    }
  }

  /** The auth check may not be ready when the element connects; wait for it, bounded. */
  private async waitForAuth(timeoutMs = 15000): Promise<void> {
    const start = Date.now()
    while (!window.tbwServices.authService.isAuthenticated) {
      if (Date.now() - start > timeoutMs) return
      await new Promise((r) => setTimeout(r, 200))
    }
  }

  /** Webflow's own tabs script binds at page load, before this markup exists. */
  private bindTabs() {
    const tabs = Array.from(this.querySelectorAll<HTMLAnchorElement>("[data-tab]"))
    const panes = Array.from(this.querySelectorAll<HTMLElement>("[data-pane]"))
    tabs.forEach((tab) => tab.addEventListener("click", (event) => {
      event.preventDefault()
      tabs.forEach((t) => t.classList.toggle("w--current", t === tab))
      panes.forEach((p) => { p.hidden = p.dataset.pane !== tab.dataset.tab })
    }))
  }
}
